/**
 * Build Calculator Prefill
 *
 * Creates a small, stable prefill payload from calculator state
 * for use when navigating to vehicle/invoice creation forms.
 */

import { normalizePlate } from '@/lib/rdwClient';
import type { CalculatorState, CalculatorPrefill } from './types';

/**
 * Validate that a license plate is in a valid format
 */
function isValidPlate(plate: string): boolean {
  const normalized = normalizePlate(plate);
  // Dutch plates are typically 6 alphanumeric characters
  return /^[A-Z0-9]{6}$/.test(normalized);
}

/**
 * Determine if a vehicle is an import based on registration dates
 */
export function detectImport(
  firstRegDate: string | null | undefined,
  firstRegDateNL: string | null | undefined
): boolean {
  if (!firstRegDate || !firstRegDateNL) {
    return false;
  }
  // If NL registration is after first registration, it's an import
  return firstRegDateNL > firstRegDate;
}

/**
 * Select the best available gross BPM value
 * Prefers RDW brutoBpm, falls back to calculated grossBpm
 */
export function selectGrossBpm(
  rdwBrutoBpm: number | null | undefined,
  calculatedGrossBpm: number | null | undefined
): number | null {
  // Prefer RDW value if it exists and is positive
  if (rdwBrutoBpm !== null && rdwBrutoBpm !== undefined && rdwBrutoBpm > 0) {
    return rdwBrutoBpm;
  }
  // Fall back to calculated value
  if (calculatedGrossBpm !== null && calculatedGrossBpm !== undefined) {
    return calculatedGrossBpm;
  }
  return null;
}

/**
 * Build a prefill payload from calculator state.
 * Returns null if required data is missing or invalid.
 */
export function buildCalculatorPrefill(state: CalculatorState): CalculatorPrefill | null {
  const { rdwData, bpm, plate } = state;

  // Require RDW data
  if (!rdwData) {
    return null;
  }

  // Require valid plate
  if (!plate || !isValidPlate(plate)) {
    return null;
  }

  const normalizedPlate = normalizePlate(plate);

  // Determine import status
  const isImport = detectImport(rdwData.firstRegDate, rdwData.firstRegDateNL);

  // Select best gross BPM
  const grossBpm = selectGrossBpm(rdwData.brutoBpm, bpm?.grossBpm);

  return {
    v: 1,
    plate: normalizedPlate,
    make: rdwData.make ?? null,
    model: rdwData.model ?? null,
    vehicleClass: rdwData.vehicleClass ?? null,
    fuelType: rdwData.fuelType ?? null,
    firstRegDate: rdwData.firstRegDate ?? null,
    firstRegDateNL: rdwData.firstRegDateNL ?? null,
    co2Gkm: rdwData.co2Gkm ?? null,
    grossBpm,
    restBpm: bpm?.restBpm ?? null,
    isImport,
    source: 'calculator',
  };
}
